import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import '../styles/SubscriptionPage.css';

function SubscriptionPage() {
    const { userProfile, updateUserPlan } = useAuth();
    const { plan } = usePermissions();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const plans = {
        basico: {
            name: 'Básico',
            price: 'Grátis',
            priceValue: 0,
            color: '#48bb78',
            features: [
                'Até 50 produtos',
                'Até 20 clientes',
                'Controle de vendas',
                'Controle de despesas',
                'Dashboard básico',
                'Suporte por email',
            ],
        },
        profissional: {
            name: 'Profissional',
            price: 'R$ 29,90/mês',
            priceValue: 29.90,
            color: '#667eea',
            features: [
                'Produtos ilimitados',
                'Clientes ilimitados',
                'Gestão de pedidos',
                'Dashboard com gráficos',
                'Relatórios intermediários',
                'Exportação CSV',
                'Suporte prioritário',
            ],
        },
        premium: {
            name: 'Premium',
            price: 'R$ 59,90/mês',
            priceValue: 59.90,
            color: '#764ba2',
            features: [
                'Tudo do Profissional',
                'Relatórios completos',
                'Análise de lucratividade',
                'Gráficos avançados',
                'Exportação completa',
                'Suporte VIP (24h)',
                'Treinamento personalizado',
            ],
        },
    };

    const currentPlan = plans[plan];

    const handleChangePlan = async (newPlan) => {
        if (newPlan === plan) return;

        const confirmed = window.confirm(
            `Deseja realmente mudar para o plano ${plans[newPlan].name}?`
        );

        if (!confirmed) return;

        try {
            setLoading(true);

            if (newPlan === 'basico') {
                // Downgrade para básico
                await updateUserPlan(newPlan, 'active');
                alert('Plano alterado para Básico com sucesso!');
            } else {
                // Upgrade ou mudança de plano pago
                // CORREÇÃO: NÃO atualiza o plano ainda!
                // Apenas salva qual plano o usuário quer no localStorage
                // O plano será atualizado somente quando o webhook confirmar o pagamento
                localStorage.setItem('pendingPlan', newPlan);
                navigate('/pagamento');
            }
        } catch (error) {
            console.error('Erro ao alterar plano:', error);
            alert('Erro ao alterar plano. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        const confirmed = window.confirm(
            'Tem certeza que deseja cancelar sua assinatura? Você será movido para o plano Básico.'
        );

        if (!confirmed) return;

        try {
            setLoading(true);
            await updateUserPlan('basico', 'active');
            alert('Assinatura cancelada. Você está agora no plano Básico.');
        } catch (error) {
            console.error('Erro ao cancelar assinatura:', error);
            alert('Erro ao cancelar assinatura. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="subscription-page">
            <div className="subscription-header">
                <h1>Gerenciar Assinatura</h1>
                <p>Atualize, faça upgrade ou cancele sua assinatura</p>
            </div>

            {/* Current Plan */}
            <div className="current-plan-section">
                <div className="current-plan-card" style={{ borderColor: currentPlan.color }}>
                    <div className="plan-badge" style={{ background: currentPlan.color }}>
                        Plano Atual
                    </div>
                    <div className="plan-info">
                        <h2>{currentPlan.name}</h2>
                        <div className="plan-price">{currentPlan.price}</div>
                        <div className="plan-status">
                            <i className="bi bi-check-circle-fill"></i>
                            <span>
                                {userProfile?.subscriptionStatus === 'active'
                                    ? 'Ativa'
                                    : 'Pendente de Pagamento'}
                            </span>
                        </div>
                    </div>

                    <div className="plan-features-summary">
                        <h3>Recursos incluídos:</h3>
                        <ul>
                            {currentPlan.features.map((feature, idx) => (
                                <li key={idx}>
                                    <i className="bi bi-check2"></i> {feature}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {plan !== 'basico' && userProfile?.subscriptionStatus === 'active' && (
                        <button
                            className="btn btn-danger"
                            onClick={handleCancelSubscription}
                            disabled={loading}
                        >
                            <i className="bi bi-x-circle me-2"></i>
                            Cancelar Assinatura
                        </button>
                    )}
                </div>
            </div>

            {/* Available Plans */}
            <div className="available-plans-section">
                <h2>Planos Disponíveis</h2>
                <p>Compare e escolha o melhor plano para seu negócio</p>

                <div className="plans-grid">
                    {Object.entries(plans).map(([planKey, planData]) => (
                        <div
                            key={planKey}
                            className={`plan-option ${planKey === plan ? 'current' : ''}`}
                        >
                            {planKey === plan && (
                                <div className="current-badge">Seu Plano</div>
                            )}

                            <h3>{planData.name}</h3>
                            <div className="price">{planData.price}</div>

                            <ul className="features-list">
                                {planData.features.map((feature, idx) => (
                                    <li key={idx}>
                                        <i className="bi bi-check-circle-fill"></i>
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            {planKey !== plan && (
                                <button
                                    className={`btn ${
                                        planKey === 'basico' ? 'btn-outline' : 'btn-primary'
                                    }`}
                                    onClick={() => handleChangePlan(planKey)}
                                    disabled={loading}
                                >
                                    {planData.priceValue > currentPlan.priceValue
                                        ? 'Fazer Upgrade'
                                        : planData.priceValue < currentPlan.priceValue
                                        ? 'Fazer Downgrade'
                                        : 'Mudar Plano'}
                                </button>
                            )}

                            {planKey === plan && (
                                <div className="current-plan-indicator">
                                    <i className="bi bi-check-lg"></i> Plano Ativo
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Payment History (Placeholder) */}
            <div className="payment-history-section">
                <h2>Histórico de Pagamentos</h2>
                <div className="history-placeholder">
                    <i className="bi bi-clock-history"></i>
                    <p>Nenhum pagamento registrado ainda.</p>
                    <small>
                        Seu histórico de pagamentos aparecerá aqui quando você efetuar cobranças.
                    </small>
                </div>
            </div>

            {/* Back to Dashboard */}
            <div className="back-link">
                <Link to="/dashboard">
                    <i className="bi bi-arrow-left"></i> Voltar ao Dashboard
                </Link>
            </div>
        </div>
    );
}

export default SubscriptionPage;
